import React, { useEffect, useState } from 'react';
import { Table, message, Input, Select, Row, Col, Button, Space, Card } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../Config/axiosConfig';
import getClassPublishedColumns from '../../Config/getClassPublishedColumns';
import UpdateClassModal from '../UpdateClassModal/UpdateClassModal';

const { Search } = Input;
const { Option } = Select;

function Published() {
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({
    instructor: null,
    search: '',
  });
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const navigate = useNavigate();

  // Fetch classes
  const fetchClasses = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/Public/get-paged-classes', {
        pageNumber: page,
        pageSize: pageSize,
      });

      const { data } = response?.data;
      console.log('Fetched class data:', data);
      
      if (data && Array.isArray(data.classes)) {
        const classesData = data.classes;
        setClasses(classesData);
        setFilteredClasses(classesData);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: data.totalCount || data.pages * pageSize || 0,
        });

        // Extract unique instructors from classes
        const uniqueInstructors = [];
        const instructorMap = new Map();
        
        classesData.forEach(cls => {
          if (cls.teacher && !instructorMap.has(cls.teacher.id)) {
            instructorMap.set(cls.teacher.id, true);
            uniqueInstructors.push({
              id: cls.teacher.id,
              name: cls.teacher.name || `${cls.teacher.firstName} ${cls.teacher.lastName}` || 'Unknown'
            });
          }
        });
        
        setInstructors(uniqueInstructors);
      } else {
        message.error('No class data received.');
        setClasses([]);
        setFilteredClasses([]);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      message.error('Error fetching classes');
      setClasses([]);
      setFilteredClasses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses(pagination.current, pagination.pageSize);
  }, []);

  // Apply filters
  const applyFilters = () => {
    let result = [...classes];

    // Filter by instructor
    if (filters.instructor) {
      result = result.filter(cls => 
        cls.teacher && cls.teacher.id === filters.instructor
      );
    }

    // Filter by search (topic/title)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(cls => 
        (cls.title && cls.title.toLowerCase().includes(searchLower)) ||
        (cls.description && cls.description.toLowerCase().includes(searchLower)) ||
        (cls.category && cls.category.name && cls.category.name.toLowerCase().includes(searchLower))
      );
    }

    setFilteredClasses(result);
    setPagination(prev => ({
      ...prev,
      current: 1, // Reset to first page when filtering
      total: result.length
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle search
  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      instructor: null,
      search: '',
    });
    setFilteredClasses(classes);
    setPagination(prev => ({
      ...prev,
      current: 1,
      total: classes.length
    }));
  };

  // Table handlers
  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    fetchClasses(current, pageSize);
  };

  const handleRowClick = (record) => {
    navigate(`/preview-class/${record.id}`);
  };

  const handleEditClass = (record) => {
    setSelectedClass(record);
    setIsModalVisible(true);
  };

  const handleUpdateSuccess = () => {
    fetchClasses(pagination.current, pagination.pageSize);
  };

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filters, classes]);

  const columns = getClassPublishedColumns(
    navigate,
    () => fetchClasses(pagination.current, pagination.pageSize),
    pagination.current,
    handleEditClass
  );

  return (
    <div className="student-admin-container">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Published Classes</h3>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchClasses(pagination.current, pagination.pageSize)}
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </div>
        }
      >
        {/* Filters Section */}
        <div style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Instructor Filter */}
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Instructor"
                style={{ width: '100%' }}
                value={filters.instructor}
                onChange={(value) => handleFilterChange('instructor', value)}
                allowClear
                disabled={instructors.length === 0}
              >
                {instructors.map(instructor => (
                  <Option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Search Filter */}
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by title, description, or category..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>

            {/* Filter Actions */}
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button 
                  onClick={applyFilters}
                  icon={<FilterOutlined />}
                  type="primary"
                >
                  Apply Filters
                </Button>
                <Button 
                  onClick={handleResetFilters}
                  disabled={!filters.instructor && !filters.search}
                >
                  Clear Filters
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Filter Summary */}
          {(filters.instructor || filters.search) && (
            <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f6ffed', borderRadius: 4 }}>
              <span style={{ marginRight: 8 }}>Active Filters:</span>
              {filters.instructor && (
                <span style={{ marginRight: 16 }}>
                  Instructor: <strong>{instructors.find(i => i.id === filters.instructor)?.name}</strong>
                </span>
              )}
              {filters.search && (
                <span>
                  Search: <strong>"{filters.search}"</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div style={{ marginBottom: 16 }}>
          Showing {filteredClasses.length} of {classes.length} classes
          {filteredClasses.length !== classes.length && (
            <span style={{ color: '#666', marginLeft: 8 }}>
              (filtered from {classes.length} total)
            </span>
          )}
        </div>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={filteredClasses}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} classes`
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </Card>

      {/* Update Modal */}
      <UpdateClassModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        classInfo={selectedClass}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </div>
  );
}

export default Published;